import { t } from "i18next";
import { isEmpty } from "lodash";
import React, { useState, useEffect } from "react";
import { Trans } from "react-i18next";
import ClassifierLabelsAPI from "../../API/classifierLabels.api";
import ClassifiersAPI from "../../API/classifiers.api";
import BaseActionButton from "../../components/Buttons/BaseActionButton";
import DeleteButton from "../../components/Buttons/DeleteButton";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import ClassifierGroupModal from "../../components/Modals/ClassifierGroupModal";
import LocalizedClassifiersModal from "../../components/Modals/LocalizedClassifiersModal";
import NestedTable from "../../components/Tables/NestedTable";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import {
    MAX_LABELS_PER_CLASSIFIER,
    MIN_LABELS_PER_CLASSIFIER,
} from "../../utils/constants";
import { longDateTime } from "../../utils/DateHelper";
import { getMappedFilters, getSessionFilters } from "../../utils/FilterHelpers";
import { getObject, setObject } from "../../utils/StorageHelpers";

const ClassifiersManagement = () => {
    //Data States
    const [classifiers, setClassifiers] = useState([]);
    const [classifiersTotalCont, setClassifiersTotalCont] = useState(0);
    const [selectedClassifierGroup, setSelectedClassifierGroup] = useState({});
    const [selectedClassifierLabel, setSelectedClassifierLabel] = useState({});

    //Filter States
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("classifiers-management-filters")
    );
    const [filtersUsed, setFiltersUsed] = useState(false);

    //Sort States
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //General States
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Classifiers-Management-itemsPerPage", true) || 10
    );

    //Modal States
    const [localizedClassifersModal, setLocalizedClassifersModal] =
        useState(false);
    const [classifierGroupModal, setClassifierGroupModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);

    const headers = [
        {
            label: t("name"),
            name: "name",
        },
        {
            label: t("description"),
            name: "description",
        },
        {
            label: t("createdAt"),
            name: "createdAt",
        },
        {
            skipSort: true,
            label: t("actions"),
            name: "actions",
        },
    ];

    const subHeaders = [
        {
            skipSort: true,
            label: t("name"),
            name: "name",
        },
        {
            skipSort: true,
            label: t("value"),
            name: "value",
        },
        {
            skipSort: true,
            label: t("createdAt"),
            name: "createdAt",
        },
        {
            skipSort: true,
            label: t("actions"),
            name: "actions",
        },
    ];

    const keys = [
        {
            name: "name",
        },
        {
            name: "description",
        },
        {
            name: "createdAt",
            parseMethod: longDateTime,
        },
        {
            name: "actions",
            element: (row) => {
                return (
                    <div className="flex items-center justify-center">
                        <BaseActionButton
                            className="table-btn table-btn-blue mr-2"
                            icon="fas fa-plus"
                            disabled={
                                row.classifierLabels.length >=
                                MAX_LABELS_PER_CLASSIFIER
                            }
                            onClick={(event) => {
                                event.stopPropagation();
                                setLocalizedClassifersModal(true);
                                setSelectedClassifierLabel({
                                    classifierId: row.id,
                                    classifierValues:
                                        row?.classifierLabels?.map(
                                            (c) => c.value
                                        ),
                                });
                            }}
                            tippy={true}
                            tippySettings={{ content: t("addLabel") }}
                        />
                        <BaseActionButton
                            className="table-btn table-btn-blue mr-2"
                            icon="fas fa-tools"
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedClassifierGroup(row);
                                setClassifierGroupModal(true);
                            }}
                            tippy={true}
                            tippySettings={{ content: t("edit") }}
                        />
                        <DeleteButton
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedClassifierGroup(row);
                                setDeleteModal(true);
                            }}
                            tippy={true}
                        />
                    </div>
                );
            },
        },
    ];

    const subKeys = [
        {
            name: "name",
        },
        {
            name: "value",
        },
        {
            name: "createdAt",
            parseMethod: longDateTime,
        },
        {
            name: "actions",
            element: (row, _, outerRow) => {
                return (
                    <div className="flex items-center justify-center">
                        <BaseActionButton
                            className="table-btn table-btn-blue mr-2"
                            icon="fas fa-globe"
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedClassifierLabel(row);
                                setLocalizedClassifersModal(true);
                            }}
                            tippy={true}
                            tippySettings={{ content: t("localization") }}
                        />
                        <DeleteButton
                            onClick={(event) => {
                                event.stopPropagation();
                                if (verifyClassifierLabelDeletable(outerRow)) {
                                    setSelectedClassifierLabel(row);
                                    setDeleteModal(true);
                                }
                            }}
                            tippy={true}
                        />
                    </div>
                );
            },
        },
    ];

    const filterOptions = [
        {
            label: t("name"),
            name: "name",
            type: "Input",
            placeholder: t("name"),
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
        },
    ];

    useEffect(() => {
        if (sortBy && sortDirection) {
            setCurrentPage(1);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        } else {
            setCurrentPage(1);
            listClassifiers(appliedFilters);
        }
    }, [sortBy, sortDirection]);

    const listClassifiers = async (
        filters,
        sortBy = null,
        sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);

        const result = await ClassifiersAPI.listClassifiers(
            itemsPerPage,
            getMappedFilters(filters),
            offset,
            sortBy,
            sortDirection
        );

        setClassifiers(result?.data);
        setClassifiersTotalCont(result?.totalCount);

        setIsLoading(false);
    };

    const deleteClassifier = async () => {
        const result = await ClassifiersAPI.deleteClassifer(
            selectedClassifierGroup?.id || ""
        );
        if (result?.deleted) {
            SuccessAlert(
                t("deleteSuccessMessage", { entity: t("classifierGroup") })
            );
            setSelectedClassifierGroup({});
            setDeleteModal(false);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        }
    };

    const createClassifierLabel = async (payload) => {
        const result = await ClassifierLabelsAPI.createClassifierLabel(payload);

        if (result?.inserted) {
            SuccessAlert(
                t("addSuccessMessage", { entity: t("classifierLabel") })
            );
            setSelectedClassifierLabel({});
            setLocalizedClassifersModal(false);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        }
    };

    const updateClassifierLabel = async (payload) => {
        const result = await ClassifierLabelsAPI.updateClassifierLabel(payload);

        if (result?.modified) {
            SuccessAlert(
                t("updateSuccessMessage", { entity: t("classifierLabel") })
            );
            setSelectedClassifierLabel({});
            setLocalizedClassifersModal(false);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        }
    };

    const deleteClassifierLabel = async () => {
        const result = await ClassifierLabelsAPI.deleteClassiferLabel(
            selectedClassifierLabel?.id || ""
        );
        if (result?.deleted) {
            SuccessAlert(
                t("deleteSuccessMessage", { entity: t("classifierLabel") })
            );
            setSelectedClassifierLabel({});
            setDeleteModal(false);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        }
    };

    const updateClassifierGroup = async (payload) => {
        const result = await ClassifiersAPI.updateClassifier(payload);
        if (result?.modified) {
            SuccessAlert(
                t("updateSuccessMessage", { entity: t("classifierGroup") })
            );
            setSelectedClassifierGroup({});
            setClassifierGroupModal(false);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        }
    };

    const createClassifierGroup = async (payload) => {
        const result = await ClassifiersAPI.createClassifier(payload);
        if (result?.inserted) {
            SuccessAlert(
                t("addSuccessMessage", { entity: t("classifierGroup") })
            );
            setSelectedClassifierGroup({});
            setClassifierGroupModal(false);
            listClassifiers(appliedFilters, sortBy, sortDirection);
        }
    };

    const isSelectedGroup = () => {
        if (
            isEmpty(selectedClassifierLabel) &&
            !isEmpty(selectedClassifierGroup)
        )
            return true;

        return false;
    };

    const verifyClassifierLabelDeletable = (mainRow) => {
        if (mainRow?.classifierLabels?.length <= MIN_LABELS_PER_CLASSIFIER) {
            ErrorAlert(
                t("minimumExceeded", {
                    entity: t("classifierLabel"),
                    count: MIN_LABELS_PER_CLASSIFIER,
                })
            );
            return false;
        }
        return true;
    };

    const applyFilters = (specificAppliedFilters) => {
        listClassifiers(specificAppliedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            listClassifiers(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Classifiers-Management-itemsPerPage", itemsPerPage, true);
        listClassifiers(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="classifiersManagement">
                            Classifiers Management
                        </Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={filterOptions}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component={"classifiers-management"}
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />
                        <button
                            className="default-orange-outline-btn ml-2"
                            onClick={() => {
                                setClassifierGroupModal(true);
                            }}
                        >
                            <i className="fas fa-plus mr-2" />
                            <Trans i18nKey="newClassifierGroup">
                                New Classifier Group
                            </Trans>
                        </button>
                    </div>
                </header>
            </div>
            <NestedTable
                headers={headers}
                subHeaders={subHeaders}
                keys={keys}
                subKeys={subKeys}
                rows={classifiers}
                subRowsKey="classifierLabels"
                isLoading={isLoading}
                isFilterUsed={filtersUsed}
                entity={t("classifiersManagement")}
                currentPage={currentPage}
                onPageChange={handlePageClick}
                totalCount={classifiersTotalCont}
                sizePerPage={itemsPerRequest}
                handleItemsPerPage={handleItemsPerPage}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
            />
            <LocalizedClassifiersModal
                isVisible={localizedClassifersModal}
                setIsVisible={setLocalizedClassifersModal}
                data={selectedClassifierLabel}
                setData={setSelectedClassifierLabel}
                updateAction={updateClassifierLabel}
                addAction={createClassifierLabel}
            />
            <ClassifierGroupModal
                isVisible={classifierGroupModal}
                setIsVisible={setClassifierGroupModal}
                data={selectedClassifierGroup}
                setData={setSelectedClassifierGroup}
                updateAction={updateClassifierGroup}
                createAction={createClassifierGroup}
            />
            <BaseAlertModal
                isVisible={deleteModal}
                setIsVisible={setDeleteModal}
                title={t("deleteTitle", {
                    purpose: t(
                        isSelectedGroup()
                            ? "classifierGroup"
                            : "classifierLabel"
                    ),
                })}
                message={t("deleteMessage", {
                    purpose: t(
                        isSelectedGroup()
                            ? "classifierGroup"
                            : "classifierLabel"
                    ),
                })}
                action={{
                    submitAction: isSelectedGroup()
                        ? deleteClassifier
                        : deleteClassifierLabel,
                }}
                type="delete"
            />
        </>
    );
};

export default ClassifiersManagement;
